/**
 * Contraseña revelada por 30 segundos (F3a). El valor lo trae la página (para
 * poder mostrar también las notas con la misma llamada auditada); acá solo el
 * temporizador, el copiar y el ocultar.
 */
import { Copy, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCopy } from '@/hooks/useVault';
import { useCountdown } from '@/hooks/useCountdown';
import { VAULT_POLICY } from '../../../../shared/types/vault';

interface RevealFieldProps {
  value: string | null;
  loading?: boolean;
  onReveal: () => void;
  onHide: () => void;
}

export function RevealField({ value, loading, onReveal, onHide }: RevealFieldProps) {
  const { t } = useTranslation();
  const copy = useCopy();
  // 30 s visible y se oculta sola (la cuenta regresiva es el hook compartido)
  const left = useCountdown(value === null ? null : VAULT_POLICY.revealSeconds, value, onHide);

  return (
    <div className="flex items-center gap-3">
      <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm">
        {value ?? '••••••••••••'}
      </code>
      {value !== null && (
        <span className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
          {t('vault.reveal.visible', 'visible {{s}} s', { s: left })}
        </span>
      )}
      <Button
        variant="outline"
        size="icon"
        onClick={value === null ? onReveal : onHide}
        disabled={!!loading}
        title={value === null ? t('vault.reveal.show', 'Revelar') : t('vault.reveal.hide', 'Ocultar')}
        aria-label={value === null ? t('vault.reveal.show', 'Revelar') : t('vault.reveal.hide', 'Ocultar')}
        data-tour="reveal"
      >
        {value === null ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </Button>
      <Button
        variant="outline"
        size="icon"
        onClick={() => value !== null && copy(value, t('vault.reveal.copied', 'Contraseña copiada'))}
        disabled={value === null}
        title={t('vault.reveal.copy', 'Copiar')}
        aria-label={t('vault.reveal.copy', 'Copiar')}
        data-tour="copy-password"
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}
